import { useQuery } from '@tanstack/react-query';
import permissionsApi, { DashboardData } from '../api/permissions';
import api from '../api/apiClient';

export interface ContractorDashboardRaw {
  activeJobs: number;
  pendingQuotes: number;
  upcomingJobs: Array<{
    id: number;
    propertyAddress: string;
    category: string;
    description: string;
    urgency: string;
    status: string;
    scheduledDate?: string;
    quoteAmount?: number;
    createdAt: string;
  }>;
  unpaidInvoices?: number;
  monthlyEarnings?: number;
}

export interface AdminDashboardStats {
  totalAgencies: number;
  activeAgencies: number;
  pendingAgencies: number;
  totalUsers: number;
  totalLeases: number;
  monthlyRevenue: number;
}

export interface AdminRecentAgency {
  id: number;
  name: string;
  abn: string;
  contactEmail: string;
  status: string;
  leaseCount: number;
  createdAt: string;
}

export interface AdminEnterpriseCode {
  id: number;
  code: string;
  description: string;
  parentAgencyId?: number;
  currentUses: number;
  maxUses?: number;
  isActive: boolean;
}

// Generic dashboard query — returns full DashboardData (used by roles that don't have a typed sub-response)
export function useDashboard() {
  return useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: () => permissionsApi.getDashboard(),
    staleTime: 30_000,
  });
}

// Renter: merges base dashboard + next-rent endpoint
export function useRenterDashboard() {
  const base = useQuery<DashboardData>({
    queryKey: ['dashboard', 'renter'],
    queryFn: () => permissionsApi.getDashboard(),
    staleTime: 30_000,
  });

  const nextRent = useQuery<{ amount: number; dueDate: string; leaseId: number } | null>({
    queryKey: ['renter', 'next-rent'],
    queryFn: async () => {
      try {
        const r = await api.get<{ amount: number; dueDate: string; leaseId: number }>('/renter/dashboard/next-rent');
        return r ?? null;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });

  const data: (DashboardData & { nextRentAmount?: number; nextDueDate?: string }) | undefined =
    base.data
      ? {
          ...base.data,
          nextRentAmount: nextRent.data?.amount,
          nextDueDate: nextRent.data?.dueDate,
        }
      : undefined;

  return {
    data,
    isLoading: base.isLoading || (nextRent.isLoading && !nextRent.isFetched),
    isError: base.isError,
    error: base.error,
    refetch: () => { base.refetch(); nextRent.refetch(); },
  };
}

// Owner dashboard
export function useOwnerDashboard() {
  return useQuery<DashboardData>({
    queryKey: ['dashboard', 'owner'],
    queryFn: () => permissionsApi.getDashboard(),
    staleTime: 30_000,
  });
}

// Agent dashboard
export function useAgentDashboard() {
  return useQuery<DashboardData>({
    queryKey: ['dashboard', 'agent'],
    queryFn: () => permissionsApi.getDashboard(),
    staleTime: 30_000,
  });
}

// Contractor dashboard — dedicated endpoint
export function useContractorDashboard() {
  return useQuery<ContractorDashboardRaw>({
    queryKey: ['dashboard', 'contractor'],
    queryFn: () => api.get<ContractorDashboardRaw>('/contractor/dashboard').then(r => r ?? { activeJobs: 0, pendingQuotes: 0, upcomingJobs: [] }),
    staleTime: 30_000,
  });
}

// Admin dashboard — 3 dedicated endpoints
export function useAdminDashboard() {
  const stats = useQuery<AdminDashboardStats>({
    queryKey: ['dashboard', 'admin', 'stats'],
    queryFn: () =>
      api.get<AdminDashboardStats>('/admin/agencies/stats').then(r => r ?? {
        totalAgencies: 0, activeAgencies: 0, pendingAgencies: 0,
        totalUsers: 0, totalLeases: 0, monthlyRevenue: 0,
      }),
    staleTime: 30_000,
  });

  const recentAgencies = useQuery<AdminRecentAgency[]>({
    queryKey: ['dashboard', 'admin', 'recent-agencies'],
    queryFn: () => api.get<AdminRecentAgency[]>('/admin/agencies/recent').then(r => r ?? []),
    staleTime: 30_000,
  });

  const enterpriseCodes = useQuery<AdminEnterpriseCode[]>({
    queryKey: ['dashboard', 'admin', 'enterprise-codes'],
    queryFn: () => api.get<AdminEnterpriseCode[]>('/admin/agencies/enterprise-codes').then(r => r ?? []),
    staleTime: 30_000,
  });

  return {
    stats: stats.data ?? null,
    recentAgencies: recentAgencies.data ?? [],
    enterpriseCodes: enterpriseCodes.data ?? [],
    isLoading: stats.isLoading,
    isError: stats.isError,
    error: stats.error,
    refetch: () => { stats.refetch(); recentAgencies.refetch(); enterpriseCodes.refetch(); },
  };
}
